/* eslint-disable max-lines-per-function */
// @ts-nocheck
import EventEmitter from 'eventemitter3';
import dayjs from 'dayjs';
import dayjsPluginIsSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Dropdown } from '../dropdown';
import { generateUID } from '../../util/common';
import type { StockData } from '../../components/types';
import KLineTooltip from '../kLineTooltip/KLineTooltip';
import { getHXKLineTooltipData } from '../../hook/preset/getHXKLineTooltipData';
import type { KLineData } from '../../helper/hxKLine';
import { getSplitLineNumber } from '../../hook/preset/getSplitLineNumber';
import { type Token } from '../../core/token';

dayjs.extend(dayjsPluginIsSameOrBefore);

if (window.HXKline && window.HXKlineChart) {
  HXKline.setHXKlineChart(HXKlineChart);
} else {
  console.warn('如果需要使用AIGC行情组件，需要额外引入HXKline和HXKlineChart组件');
}

type EventTypes = {
  updateTooltip: (event: { date: string | null }) => void;
  brushing: (event: { startDate?: string; endDate?: string }) => void;
};

type Option = {
  container: HTMLElement;
  stockData: StockData[number];
  /**
   * @see https://khtest.10jqka.com.cn/kodexplorer/data/User/wangyifan/home/HXKlineChart/docs/guide/datafeed-datasource.html#%F0%9F%93%9A-%E4%B8%AA%E8%82%A1%E4%BF%A1%E6%81%AF%E6%95%B0%E6%8D%AE%E7%BB%93%E6%9E%84
   */
  chartOption?: {
    time_period: 'day_1' | 'week_1' | 'month_1';
    trade_date: 0 | [Date, Date];
  };
  hxKLineVerifyInfo: unknown;
  layout?: {
    currentLayout?: string;
    layoutInfo?: {
      [key: string]: string;
    };
  };
  cardDom?: HTMLElement;
  token: Partial<Token>;
};

type EventHandler = (event: Event) => void;

interface EventListeners {
  [id: string]: {
    [event: string]: EventHandler[];
  };
}

export default class SimpleHXKLine extends EventEmitter<EventTypes> {
  private __option: Option;

  private __$chart!: HTMLElement;

  private __$cardDom!: HTMLElement;

  private __dropdown!: Dropdown;

  private __tooltip!: KLineTooltip;

  hxKLine!: ReturnType<typeof HXKline.initChart>;

  private __isKLineLoadingSuccessful = false;

  /** 防止触发 tooltip 更新时又触发事件，陷入死循环 */
  private __kLineAxisPointerEventSilent = false;

  private __brushPressTimer: ReturnType<typeof setTimeout>;

  private __brushTempTimers: ReturnType<typeof setTimeout>[] = [];

  private __eventListeners: EventListeners = {};

  constructor(option: Option) {
    super();

    this.__option = option;

    this.__initialize();
  }

  private __initialize() {
    const self = this;

    HXKline.verifyProjectPermission(self.__option.hxKLineVerifyInfo);

    const dropdownData = [
      { key: 'day_1', text: `${self.__option.stockData.name}(日K)` },
      { key: 'week_1', text: `${self.__option.stockData.name}(周K)` },
      { key: 'month_1', text: `${self.__option.stockData.name}(月K)` }
    ];
    let selectedTimePeriod = self.__option.chartOption?.time_period || dropdownData[0].key;

    // * --------------------------------------- dom
    const $chart = document.createElement('div');
    const $dropdown = document.createElement('div');
    const $tooltipContainer = document.createElement('div');
    const option = this.__option;

    $chart.setAttribute('id', generateUID());
    $chart.style.cssText = 'position: relative; width: 100%; height: 100%;';
    $dropdown.style.cssText = 'position: absolute; top: 4px; left: 50px;';
    $tooltipContainer.style.cssText = 'position: absolute; top: 4px; left: 50px;';

    if (dropdownData.length <= 1) {
      $dropdown.style.display = 'none';
    }

    option.container.appendChild($chart);
    this.__$chart = $chart;

    function updateTooltipContainerLeft() {
      $tooltipContainer.style.left = `${
        $dropdown.offsetLeft + $dropdown.getBoundingClientRect().width + 12
      }px`;
    }

    // * --------------------------------------- tooltip
    const kLineTooltip: KLineTooltip = new KLineTooltip($tooltipContainer);
    kLineTooltip.hide();
    self.__tooltip = kLineTooltip;

    // * --------------------------------------- hxkLine
    function renderHXKLine() {
      self.__isKLineLoadingSuccessful = false;

      if (self.hxKLine) {
        HXKline.disposeChart(
          (self.hxKLine._chartContainer as HTMLElement).parentElement?.getAttribute('id'),
          (...args) => {
            console.log('hxKLine destroy', ...args);
          }
        );
      }

      /** 行情图容器高度 */
      const chartHeight = $chart.clientHeight;
      /** 没有指标图时，行情图的x轴高度固定为24px */
      const DEFAULT_XAXIS_HEIGHT = 24;

      /**
       * @see https://khtest.10jqka.com.cn/kodexplorer/data/User/wangyifan/home/HXKlineChart/docs/guide/datafeed-datasource.html#%F0%9F%93%9A-%E4%B8%AA%E8%82%A1%E4%BF%A1%E6%81%AF%E6%95%B0%E6%8D%AE%E7%BB%93%E6%9E%84
       */
      const stockInfo = {
        id: generateUID(),
        code: self.__option.stockData.code,
        market: self.__option.stockData.market,
        data_class: 'kline',
        trade_date: 0,
        adjust_type: 'forward',
        ...(self.__option.chartOption || {}),
        time_period: selectedTimePeriod
      };
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const hxKLine = HXKline.initChart(
        $chart.getAttribute('id'),
        stockInfo,
        /**
         * @see https://khtest.10jqka.com.cn/kodexplorer/data/User/wangyifan/home/HXKlineChart/docs/guide/chart-api.html
         */
        {
          locale: 'zh_CN',
          dateFormat: 'YYYY-MM-DD',
          styles: 'customTheme'
        },
        (...args) => {
          console.log(...args);

          if (args[0] !== 'success') {
            return;
          }
          self.__isKLineLoadingSuccessful = true;

          hxKLine.setZoomEnabled(false);
          hxKLine.setScrollEnabled(false);
          hxKLine.setBarSpaceLimit({ min: 1, max: 10000 });
          hxKLine.zoomToShowAllData();
          hxKLine.setStyles({
            xAxis: {
              tickText: {
                family: 'Microsoft YaHei'
              }
            },
            yAxis: {
              position: 'left',
              splitNum: getSplitLineNumber(chartHeight - DEFAULT_XAXIS_HEIGHT) - 1,
              tickText: {
                family: 'Microsoft YaHei'
              },
              axisLine: {
                show: false
              },
              tickLine: {
                show: false
              }
            },
            candle: {
              priceMark: {
                show: false
              },
              tooltip: {
                showRule: 'none',
                custom: []
              }
            },
            crosshair: {
              horizontal: {
                show: false
              }
            }
          });
          // hxKLine.setPaneOptions({
          //   id: 'pane_1',
          //   gap: {
          //     top: 4,
          //     bottom: 4
          //   }
          // });

          // 处理 dom 相关的问题
          const $xAxis = $chart.querySelector(
            'div > div:nth-child(2) > div:nth-child(1)'
          ) as HTMLElement;
          const $yAxis = $chart.querySelector('div > div > div:nth-child(2)') as HTMLElement;
          $xAxis.style.cursor = 'default';
          $yAxis.style.cursor = 'default';

          self.__bindBrushEvents(self.hxKLine, brushing => {
            if (!brushing) {
              self.emit('brushing', {});

              return;
            }

            /**
             * 刷选后需要禁止行情图滚动和缩放功能，由于滚动到指定时间戳的api需要时间，所以不能立马禁止行情图滚动和缩放功能
             * 并且获取当前视窗内数据getVisibleDataList的api也需要异步才能获取到正确日期
             * 16ms的设置是为了尽可能避免用户在刷选后立马滚动或缩放行情图
             */
            self.__brushTempTimers.push(
              setTimeout(() => {
                self.hxKLine.setScrollEnabled(false);
                self.hxKLine.setZoomEnabled(false);
                const dataset = hxKLine.getVisibleDataList() as { data: KLineData }[];
                self.emit('brushing', {
                  startDate: dayjs(dataset[0].data.timestamp).format('YYYY-MM-DD'),
                  endDate: dayjs(dataset[dataset.length - 1].data.timestamp).format('YYYY-MM-DD')
                });
              }, 16)
            );
          });
        }
      );
      /**
       * - gap 限制主图内容的上下间隔
       * - scrollZoomEnabled 限制y轴的拖动
       */
      hxKLine.setPaneOptions({
        id: 'candle_pane',
        gap: {
          top: 0.05,
          bottom: 0.05
        },
        axisOptions: { scrollZoomEnabled: false }
      });

      // * ----------------------------------------------------
      hxKLine.subscribeAction('onCrosshairChange', (...args) => {
        if (self.__kLineAxisPointerEventSilent) {
          self.__kLineAxisPointerEventSilent = false;

          return;
        }
        self.__kLineAxisPointerEventSilent = false;

        if (!args[0].kLineData) {
          self.showTooltip({
            date: null,
            triggerEvent: true
          });

          return;
        }

        const kLineData = args[0].kLineData as KLineData;

        self.showTooltip({
          date: dayjs(kLineData.timestamp).format('YYYY-MM-DD'),
          triggerEvent: true
        });

        const $yAxis = $chart.querySelector('div > div > div:nth-child(2) > canvas');
        const yAxisWidth = $yAxis?.getBoundingClientRect().width;

        $dropdown.style.left = `${yAxisWidth - 4}px`;
        updateTooltipContainerLeft();
      });

      self.hxKLine = hxKLine;
    }

    $chart.onmouseleave = () => {
      self.showTooltip({ date: null, triggerEvent: true });
      self.__tooltip.hide();
    };

    renderHXKLine();

    $chart.appendChild($dropdown);
    $chart.appendChild($tooltipContainer);

    const dropdown = new Dropdown($dropdown, {
      theme: 'kline',
      data: dropdownData,
      defaultValue: selectedTimePeriod,
      listMaxHeight: $chart.getBoundingClientRect().height * 0.9,
      token: option.token?.dvDropdown
    });
    updateTooltipContainerLeft();

    // * ---------------------------------------
    dropdown.on('change', data => {
      selectedTimePeriod = data.key;
      renderHXKLine();
      updateTooltipContainerLeft();
    });

    this.__dropdown = dropdown;
  }

  destroy() {
    this.__clearBrushTimer();
    this.removeAllEventListeners();

    HXKline.disposeChart(
      (this.hxKLine._chartContainer as HTMLElement).parentElement?.getAttribute('id'),
      (...args) => {
        console.log('hxKLine destroy', ...args);
      }
    );
    this.__dropdown.destroy();
    this.__tooltip?.destroy();

    this.__$chart.parentElement!.removeChild(this.__$chart);
  }

  addEventListenerToAll(selector: HTMLElement, event: string, handler: EventHandler) {
    const self = this;
    const eventListeners = self.__eventListeners;
    selector.addEventListener(event, handler);
    // 将监听器存储在全局对象中
    if (!eventListeners[selector.id]) {
      eventListeners[selector.id] = {};
    }
    if (!eventListeners[selector.id][event]) {
      eventListeners[selector.id][event] = [];
    }
    eventListeners[selector.id][event].push(handler);
  }

  removeAllEventListeners() {
    const self = this;
    const eventListeners: EventListeners = self.__eventListeners || {};
    const elementsWithListeners = Object.entries(eventListeners);
    elementsWithListeners.forEach(([id, eventHandlers]) => {
      const element = document.getElementById(id);
      if (!element) {
        return;
      }

      Object.entries(eventHandlers).forEach(([event, handlers]) => {
        handlers.forEach(handler => {
          element.removeEventListener(event, handler);
        });
      });
    });

    self.__eventListeners = {};
  }

  private __clearBrushTimer() {
    clearTimeout(this.__brushPressTimer);
    this.__brushPressTimer = null;
    this.__brushTempTimers.forEach(timer => clearTimeout(timer));
    this.__brushTempTimers = [];
  }

  /**
   * 刷选交互
   */
  private __bindBrushEvents(
    chartInstance,
    callback = (brushing = false) => {},
    bgColor = 'rgba(0,0,0,.05)'
  ) {
    const self = this;
    const { cardDom } = self.__option;
    // const cardDomId = generateUID();
    // cardDom.id = cardDomId;
    self.__$cardDom = cardDom;
    // 记录当前的右侧坐标和柱子宽度
    const LONG_PRESS_THRESHOLD = 4;
    const beforeRightIndex = chartInstance.getVisibleDataList().at(-1).dataIndex;
    const beforeBarWith = chartInstance.getBarSpace();
    const chartDom = chartInstance.getDom('candle_pane', 'main') as HTMLElement;
    const chartDomId = generateUID();
    chartDom.id = chartDomId;
    const pictureWidth = chartDom.clientWidth;
    let isLongPress = false;
    const longPressTime = 0;
    let startPoint: { offsetX: number; offsetY: number; clientX: number; clientY: number };
    let canvas: HTMLCanvasElement;
    let isBrushDone = false;

    self.__clearBrushTimer();

    // 监听chartDom的长摁开启刷选事件
    const enableFiltering = e => {
      e.preventDefault();
      // e.stopPropagation();

      if (isBrushDone || self.__brushTempTimers.length) {
        return;
      }

      self.__clearBrushTimer();
      self.__brushPressTimer = setTimeout(() => {
        chartDom.style.cursor = 'nwse-resize';

        isLongPress = true;

        chartInstance.setScrollEnabled(false);
        chartInstance.setZoomEnabled(false);

        self.__brushPressTimer = null;
      }, longPressTime);

      startPoint = {
        offsetX: e.offsetX,
        offsetY: e.offsetY,
        clientX: e.clientX,
        clientY: e.clientY
      };
    };
    self.addEventListenerToAll(chartDom, 'mousedown', enableFiltering);

    // 监听chartDom移动执行刷选事件
    const performFiltering = e => {
      e.preventDefault();
      // e.stopPropagation();

      if (
        self.__brushPressTimer &&
        (Math.abs(e.clientX - startPoint.clientX) > LONG_PRESS_THRESHOLD ||
          Math.abs(e.clientY - startPoint.clientY) > LONG_PRESS_THRESHOLD)
      ) {
        clearTimeout(self.__brushPressTimer);
        self.__brushPressTimer = null;

        return;
      }

      if (!isLongPress) {
        return;
      }

      self.__clearBrushTimer();

      // 将指针样式设为选中样式
      // 在chartDom中创建canvas，并根据startPoint和当前鼠标位置画矩形区域
      if (canvas) {
        chartDom.removeChild(canvas);
      }
      canvas = document.createElement('canvas');
      canvas.width = chartDom.clientWidth;
      canvas.height = chartDom.clientHeight;
      canvas.style.position = 'absolute';
      canvas.style.top = 0;
      canvas.style.left = 0;
      canvas.style.zIndex = 100;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = bgColor;
      // ctx.fillRect(startPoint.x, startPoint.y, e.offsetX - startPoint.x, e.offsetY - startPoint.y);
      ctx.fillRect(startPoint.offsetX, 0, e.clientX - startPoint.clientX, chartDom.clientHeight);
      chartDom.appendChild(canvas);
    };
    self.addEventListenerToAll(chartDom, 'mousemove', performFiltering);

    // 监听chartDom结束刷选事件
    const disableFiltering = e => {
      e.preventDefault();
      // e.stopPropagation();
      self.__clearBrushTimer();

      chartDom.style.cursor = 'default';
      if (canvas) {
        chartDom.removeChild(canvas);
      }
      canvas = null;

      if (
        Math.abs(e.clientX - startPoint.clientX) < LONG_PRESS_THRESHOLD &&
        Math.abs(e.clientY - startPoint.clientY) < LONG_PRESS_THRESHOLD
      ) {
        isLongPress = false;

        return;
      }

      if (!isLongPress) {
        return;
      }

      isLongPress = false;

      chartInstance.setScrollEnabled(true);
      chartInstance.setZoomEnabled(true);

      let startPointIndex = chartInstance.convertFromPixel(
        { x: startPoint.offsetX, y: 0 },
        {
          paneId: 'candle_pane'
        }
      ) as {
        dataIndex: number;
        timestamp: number;
        value: number;
      };
      let endPointIndex = chartInstance.convertFromPixel(
        {
          x: startPoint.offsetX + (e.clientX - startPoint.clientX),
          y: 0
        },
        {
          paneId: 'candle_pane'
        }
      ) as {
        dataIndex: number;
        timestamp: number;
        value: number;
      };
      if (startPointIndex.timestamp > endPointIndex.timestamp) {
        [startPointIndex, endPointIndex] = [endPointIndex, startPointIndex];
      }

      const nowBarCount = endPointIndex.dataIndex - startPointIndex.dataIndex + 1;
      const targetBarWith = pictureWidth / nowBarCount;
      chartInstance.setBarSpace(targetBarWith);
      chartInstance.scrollToTimestamp(endPointIndex.timestamp);
      callback?.(true);
      isBrushDone = true;
    };
    self.addEventListenerToAll(chartDom, 'mouseup', disableFiltering);

    // 监听鼠标移出cardDom事件
    const initFilteringState = e => {
      e.preventDefault();
      // e.stopPropagation();

      self.__clearBrushTimer();

      if (isLongPress) {
        chartDom.style.cursor = 'default';
        if (canvas) {
          chartDom.removeChild(canvas);
        }
        canvas = null;
      }
      if (isBrushDone) {
        callback?.(false);
      }

      isLongPress = false;
      isBrushDone = false;

      if (beforeBarWith) {
        chartInstance.setBarSpace(beforeBarWith);
      }
      if (beforeRightIndex) {
        chartInstance.scrollToDataIndex(beforeRightIndex);
      }
      chartInstance.setScrollEnabled(false);
      chartInstance.setZoomEnabled(false);
    };
    self.addEventListenerToAll(cardDom, 'mouseleave', initFilteringState);
  }

  private __updateKLineAxisPointer(data: { x?: number }) {
    this.__kLineAxisPointerEventSilent = true;
    this.hxKLine.executeAction('onCrosshairChange', data);
  }

  private __updateTooltip(date: string | null, triggerAxisPointer = false) {
    if (!date) {
      this.__tooltip.hide();
      this.__updateKLineAxisPointer({});

      return;
    }

    const dataset = this.hxKLine.getDataList() as KLineData[];
    const dataIndex = dataset.findLastIndex(item => dayjs(item.timestamp).isSameOrBefore(date));

    if (dataIndex <= -1) {
      this.__tooltip.hide();
      this.__updateKLineAxisPointer({});

      return;
    }

    if (triggerAxisPointer) {
      const result = this.hxKLine.convertToPixel({ dataIndex }, {}) as { x: number };

      this.__updateKLineAxisPointer({ x: result?.x });
    }

    const { defaultTooltipData: defaultKLineTooltip } = getHXKLineTooltipData(
      this.hxKLine,
      'kline',
      dataIndex
    );

    let showTooltip;
    const layout = this.__option.layout.currentLayout;
    const LAYOUT = this.__option.layout.layoutInfo;
    showTooltip = defaultKLineTooltip;
    if (layout === LAYOUT.SMALL || layout === LAYOUT.SMALL_1_2) {
      // 设计师规定，卡片宽度较小时只显示两项tooltip字段
      showTooltip = showTooltip.slice(0, 1);
    }
    if (layout === LAYOUT.SMALL_2_1 || layout === LAYOUT.LARGE) {
      showTooltip = showTooltip.slice(0, 5);
    }

    this.__tooltip.show(showTooltip);
  }

  showTooltip(params: {
    date: string | null;
    triggerEvent: boolean;
    triggerAxisPointer?: boolean;
  }) {
    if (!this.__isKLineLoadingSuccessful) {
      return;
    }

    this.__updateTooltip(params.date, params.triggerAxisPointer);

    if (params?.triggerEvent) {
      this.emit('updateTooltip', { date: params.date });
    }
  }
}
